import { match } from "ts-pattern";

import type { QuestionNumberUnit } from "@tycho/web-bindings/types";

/** 5 → "5", 5.5 → "5.5", 5.51 → "5.51": up to two decimals, trailing zeroes
 *  trimmed. Two decimals is exact for whole basis points, and the rounding also
 *  absorbs float noise from fraction → percent conversions (0.0551 * 100). */
export function formatPercent(percent: number): string {
  return percent.toLocaleString("en-US", { maximumFractionDigits: 2, useGrouping: false });
}

export function peopleOrPerson(count: number): string {
  return count === 1 ? "person" : "people";
}

export function backerOrBackers(count: number): string {
  return count === 1 ? "backer" : "backers";
}

export function formatUSDLong(value: number, roundToDollar = false): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: roundToDollar ? 0 : 2,
  }).format(value);
}

/**
 * Formats a whole-dollar amount for controlled inputs (`$1,500`). Returns `""`
 * for `undefined` / non-finite values so callers can show an empty field.
 */
export function formatDollars(value: number | undefined): string {
  if (value === undefined) return "";
  if (!Number.isFinite(value)) return "";
  return formatUSDLong(Math.trunc(value), true);
}

/**
 * Parses a possibly formatted dollar string into a whole-dollar integer.
 * Strips non-digits (`"$1,500"` → `1500`). Returns `undefined` when empty so
 * callers can distinguish "no amount yet" from `$0`.
 */
export function parseDollars(input: string): number | undefined {
  const digits = input.replace(/\D/g, "");
  if (digits.length === 0) return undefined;
  const parsed = Number.parseInt(digits, 10);
  if (Number.isNaN(parsed)) return undefined;
  return parsed;
}

/**
 * Formats a whole share count for controlled inputs (`1,500`). Returns `""` for
 * `undefined` / non-finite values so callers can show an empty field.
 *
 * The share-side twin of `formatDollars`: same whole-number, grouped-thousands
 * shape, without the currency symbol. Shares are a COUNT, so the unit ("shares")
 * belongs beside the field as a label, not inside the value.
 */
export function formatShares(value: number | undefined): string {
  if (value === undefined) return "";
  if (!Number.isFinite(value)) return "";
  return Math.trunc(value).toLocaleString("en-US");
}

/**
 * Parses a possibly formatted share string into a whole-share integer.
 * Strips non-digits (`"1,500"` → `1500`). Returns `undefined` when empty so
 * callers can distinguish "no quantity yet" from `0`.
 *
 * Identical in behaviour to `parseDollars` — fractional shares aren't sold, so
 * both units are whole-integer — but kept separate so a future change to one
 * (share lots, or dollar cents) can't silently retune the other.
 */
export function parseShares(input: string): number | undefined {
  const digits = input.replace(/\D/g, "");
  if (digits.length === 0) return undefined;
  const parsed = Number.parseInt(digits, 10);
  if (Number.isNaN(parsed)) return undefined;
  return parsed;
}

export function formatUSDShort(value: number): string {
  const formatted = formatNumberToShort(value);
  return `$${formatted}`;
}

// don't compress thousands
export const SHOW_TEN_THOUSAND = 10_001; // make it so 10,000 looks "longer" than 1,000
export const SHOW_THOUSANDS = 10_000;

export type NumberToShortOptions = {
  precision?: number;
  showThousands?: boolean;
  showTenThousand?: boolean;
};

export function formatNumberToShort(value: number, opts?: NumberToShortOptions): string {
  const { precision = 0, showThousands = false, showTenThousand = false } = opts ?? {};
  const absValue = Math.abs(value);
  let suffix = "";
  let divisor = 1;

  if (absValue >= 1_000_000_000) {
    divisor = 1_000_000_000;
    suffix = "B";
  } else if (absValue >= 1_000_000) {
    divisor = 1_000_000;
    suffix = "M";
  } else if (
    absValue >= (showThousands ? SHOW_THOUSANDS : showTenThousand ? SHOW_TEN_THOUSAND : 1_000)
  ) {
    divisor = 1_000;
    suffix = "k";
  }

  const shortValue = value / divisor;
  let actualPrecision = 0;
  // for small numbers, use the precision passed in; for larger numbers, use the precision that looks best
  if (absValue < 1000) {
    actualPrecision = precision;
  } else if (shortValue < 10) {
    actualPrecision = 2;
  } else if (shortValue < 100) {
    actualPrecision = 1;
  } else {
    actualPrecision = 0;
  }

  const formatter = new Intl.NumberFormat("en-US", {
    style: "decimal",
    maximumFractionDigits: actualPrecision,
  });

  return formatter.format(shortValue) + suffix;
}

export function formatCompactWholeNumberWithUnit(
  preFormatted: string,
  unit: QuestionNumberUnit["tag"],
): string {
  return match(unit)
    .with("Dollars", () => `$${preFormatted}`)
    .with("DollarsPerMonth", () => `$${preFormatted}/mo`)
    .with("People", () => {
      const count = preFormatted === "1" ? 1 : 2;
      return `${preFormatted} ${peopleOrPerson(count)}`;
    })
    .with("SquareFeet", () => `${preFormatted} sqft`)
    .exhaustive();
}
